import AuthService from '../Security/AuthService.js';

let inactivityTimeout = null;
let warningTimeout = null;
let tokenRefreshInterval = null;

let isTracking = false;
let isRefreshingToken = false;
let lastRefreshAttempt = 0;

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // logout after 30 minutes
const WARNING_AT_MS = 25 * 60 * 1000;       // warn at 25 minutes

// Backend JWT is 30 minutes.
// Check every minute, but only refresh when token has 5 minutes or less left.
const REFRESH_WHEN_LESS_THAN_MS = 5 * 60 * 1000;

// Prevent duplicate refresh attempts.
const REFRESH_THROTTLE_MS = 60 * 1000;

// Check token once per minute while logged in.
// This does NOT extend inactivity.
// It only refreshes if the user is still inside the allowed 30-minute window.
const TOKEN_REFRESH_CHECK_INTERVAL_MS = 60 * 1000;

const SESSION_EXPIRES_KEY = 'sessionExpires';

function isLoggedIn() {
  return !!localStorage.getItem('jwt');
}

function setSessionExpiration() {
  sessionStorage.setItem(
    SESSION_EXPIRES_KEY,
    String(Date.now() + INACTIVITY_LIMIT_MS)
  );
}

function getJwtPayload(token) {
  try {
    const parts = token.split('.');

    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((char) => {
          return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('Could not read JWT payload:', error);
    return null;
  }
}

function getTokenExpirationMs() {
  const token = localStorage.getItem('jwt');

  if (!token) {
    return null;
  }

  const payload = getJwtPayload(token);

  if (!payload || !payload.exp) {
    return null;
  }

  // JWT exp is seconds. Date.now() is milliseconds.
  return payload.exp * 1000;
}

function isTokenExpired() {
  const expirationMs = getTokenExpirationMs();

  if (!expirationMs) {
    return false;
  }

  return Date.now() >= expirationMs;
}

function shouldRefreshTokenSoon() {
  const expirationMs = getTokenExpirationMs();

  if (!expirationMs) {
    return false;
  }

  const timeLeft = expirationMs - Date.now();

  return timeLeft > 0 && timeLeft <= REFRESH_WHEN_LESS_THAN_MS;
}

function isFrontendSessionExpired() {
  const expirationTime = sessionStorage.getItem(SESSION_EXPIRES_KEY);

  if (!expirationTime) {
    return false;
  }

  return Date.now() > parseInt(expirationTime, 10);
}

async function refreshTokenIfNeeded() {
  if (!isLoggedIn()) {
    return;
  }

  // If frontend inactivity session expired, do not refresh. Log out.
  if (isFrontendSessionExpired()) {
    logoutUser(true);
    return;
  }

  if (isRefreshingToken) {
    return;
  }

  const now = Date.now();

  if (now - lastRefreshAttempt < REFRESH_THROTTLE_MS) {
    return;
  }

  // Only refresh during the last 5 minutes of the JWT.
  if (!shouldRefreshTokenSoon()) {
    return;
  }

  isRefreshingToken = true;
  lastRefreshAttempt = now;

  try {
    const authService = new AuthService();
    await authService.refreshToken();

    console.log('JWT refreshed successfully.');
  } catch (error) {
    console.warn('JWT refresh failed:', error);

    if (isTokenExpired()) {
      logoutUser(false);
    }
  } finally {
    isRefreshingToken = false;
  }
}

async function refreshTokenAfterWarning() {
  if (!isLoggedIn()) {
    return;
  }

  /*
    Important safety check:
    If the user left the alert open too long and the 30-minute inactivity
    session expired, do NOT refresh. Log them out.
  */
  if (isFrontendSessionExpired()) {
    logoutUser(true);
    return;
  }

  try {
    const authService = new AuthService();

    // User clicked OK before the 30-minute limit, so refresh is allowed.
    await authService.refreshToken();

    // Count pressing OK as user activity.
    setSessionExpiration();

    // Restart the full inactivity timer.
    resetInactivityTimer();

    console.log('JWT refreshed after inactivity warning.');
  } catch (error) {
    console.warn('Refresh after warning failed:', error);

    if (isTokenExpired()) {
      logoutUser(false);
      return;
    }

    /*
      If the token is not expired, keep the user signed in and restart timer.
      This avoids logging users out because of a temporary refresh/network issue.
    */
    setSessionExpiration();
    resetInactivityTimer();
  }
}

function startTokenRefreshInterval() {
  if (tokenRefreshInterval) {
    return;
  }

  tokenRefreshInterval = setInterval(() => {
    if (!isLoggedIn()) {
      stopTokenRefreshInterval();
      return;
    }

    if (isFrontendSessionExpired()) {
      logoutUser(true);
      return;
    }

    refreshTokenIfNeeded();
  }, TOKEN_REFRESH_CHECK_INTERVAL_MS);
}

function stopTokenRefreshInterval() {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
}

function addActivityListeners() {
  if (isTracking) return;
  isTracking = true;

  document.addEventListener('mousemove', resetInactivityTimer, true);
  document.addEventListener('mousedown', resetInactivityTimer, true);
  document.addEventListener('keydown', resetInactivityTimer, true);
  document.addEventListener('keyup', resetInactivityTimer, true);
  document.addEventListener('click', resetInactivityTimer, true);

  // Form activity
  document.addEventListener('input', resetInactivityTimer, true);
  document.addEventListener('change', resetInactivityTimer, true);
  document.addEventListener('focusin', resetInactivityTimer, true);
  document.addEventListener('paste', resetInactivityTimer, true);
  document.addEventListener('select', resetInactivityTimer, true);

  // Scrolling / mobile
  document.addEventListener('scroll', resetInactivityTimer, true);
  document.addEventListener('wheel', resetInactivityTimer, true);
  document.addEventListener('touchstart', resetInactivityTimer, true);
  document.addEventListener('touchmove', resetInactivityTimer, true);
}

function removeActivityListeners() {
  if (!isTracking) return;
  isTracking = false;

  document.removeEventListener('mousemove', resetInactivityTimer, true);
  document.removeEventListener('mousedown', resetInactivityTimer, true);
  document.removeEventListener('keydown', resetInactivityTimer, true);
  document.removeEventListener('keyup', resetInactivityTimer, true);
  document.removeEventListener('click', resetInactivityTimer, true);

  document.removeEventListener('input', resetInactivityTimer, true);
  document.removeEventListener('change', resetInactivityTimer, true);
  document.removeEventListener('focusin', resetInactivityTimer, true);
  document.removeEventListener('paste', resetInactivityTimer, true);
  document.removeEventListener('select', resetInactivityTimer, true);

  document.removeEventListener('scroll', resetInactivityTimer, true);
  document.removeEventListener('wheel', resetInactivityTimer, true);
  document.removeEventListener('touchstart', resetInactivityTimer, true);
  document.removeEventListener('touchmove', resetInactivityTimer, true);
}

function clearTimers() {
  if (warningTimeout) clearTimeout(warningTimeout);
  if (inactivityTimeout) clearTimeout(inactivityTimeout);

  warningTimeout = null;
  inactivityTimeout = null;
}

function resetInactivityTimer() {
  if (!isLoggedIn()) {
    clearTimers();
    removeActivityListeners();
    stopTokenRefreshInterval();
    sessionStorage.removeItem(SESSION_EXPIRES_KEY);
    return;
  }

  clearTimers();

  // Only real user activity extends frontend inactivity session.
  setSessionExpiration();

  // Activity can also trigger refresh if token is already in the last 5 minutes.
  refreshTokenIfNeeded();

  warningTimeout = setTimeout(async () => {
    if (!isLoggedIn()) {
      return;
    }

    alert(
      'You will be logged out in 5 minutes due to inactivity. Press OK to stay signed in.'
    );

    /*
      If user clicks OK before 30 minutes, this refreshes and restarts the timer.
      If user waits too long and the 30-minute inactivity window already expired,
      refreshTokenAfterWarning() will log them out instead of refreshing.
    */
    await refreshTokenAfterWarning();
  }, WARNING_AT_MS);

  inactivityTimeout = setTimeout(() => {
    if (isLoggedIn()) {
      logoutUser(true);
    }
  }, INACTIVITY_LIMIT_MS);
}

function checkSessionExpiration() {
  const token = localStorage.getItem('jwt');

  if (!token) {
    clearTimers();
    removeActivityListeners();
    stopTokenRefreshInterval();
    sessionStorage.removeItem(SESSION_EXPIRES_KEY);
    return;
  }

  if (isFrontendSessionExpired()) {
    logoutUser(false);
    return;
  }

  addActivityListeners();

  // Start or refresh the 30-minute inactivity timer.
  resetInactivityTimer();

  // Also check token once per minute so user does not need to type/click
  // exactly inside the refresh window.
  startTokenRefreshInterval();
}

async function logoutUser(isInactivity = false) {
  const role = localStorage.getItem('role');

  clearTimers();
  removeActivityListeners();
  stopTokenRefreshInterval();

  const authService = new AuthService();

  try {
    await authService.logout();
  } catch (error) {
    console.warn('Logout API failed:', error);
  }

  sessionStorage.clear();

  localStorage.removeItem('jwt');
  localStorage.removeItem('role');
  localStorage.removeItem('patientId');
  localStorage.removeItem('doctorId');
  localStorage.removeItem('office_id');
  localStorage.removeItem('username');

  alert(
    isInactivity
      ? 'You have been logged out due to inactivity.'
      : 'Your session has expired. Please log in again.'
  );

  if (role === 'PATIENT') {
    window.location.href = '/login/patient';
  } else {
    window.location.href = '/login/doctor';
  }
}

export {
  checkSessionExpiration,
  resetInactivityTimer,
  logoutUser,
  refreshTokenIfNeeded
};